import React, { useCallback, useEffect, useMemo, useState } from "react";

import dlt from "../../static/images/delete.png";
import plus from "../../static/images/plus.png";
import DeleteConfirmModal from "./delete-confirm";
import { commonFunction } from "../common-functions";
import {
  buildCombinedSheetFieldKey,
  sanitizeAiMarkerKeySegment,
} from "../../utils";

function collectRowFieldKeys(rows) {
  const set = new Set();
  rows.forEach((row) => {
    if (row && typeof row === "object" && !Array.isArray(row)) {
      Object.keys(row).forEach((k) => set.add(k));
    }
  });
  return Array.from(set);
}

function buildTableInputsFromExtractedData(extracted) {
  const inputs = {};
  if (!extracted?.data) return inputs;
  extracted.data.forEach((sheet, sheetIndex) => {
    if (!sheet?.data) return;
    Object.keys(sheet.data).forEach((tableKey) => {
      const value = sheet.data[tableKey];
      if (!Array.isArray(value) || value.length === 0) return;
      const tableId = `${sheetIndex}::${tableKey}`;
      const fields = collectRowFieldKeys(value);
      inputs[tableId] = {
        selectedIndex: [],
        isValid: false,
        fields: Object.fromEntries(fields.map((f) => [f, ""])),
      };
    });
  });
  return inputs;
}

function dataStructureSignature(extracted) {
  if (!extracted?.data) return "";
  return JSON.stringify(
    extracted.data.map((sheet) => ({
      sheet: sheet.sheet_name,
      tables: Object.keys(sheet.data || {}).filter((k) => {
        const v = sheet.data[k];
        return Array.isArray(v) && v.length > 0;
      }),
    }))
  );
}

function formatTableCell(value) {
  if (value === undefined || value === null || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** Editable text representation for scalar (non-table) fields */
function scalarToInputString(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function parseScalarInputForStore(prev, rawString) {
  if (typeof prev === "number") {
    const t = rawString.trim();
    if (t === "") return "";
    const n = Number(rawString);
    return Number.isNaN(n) ? rawString : n;
  }
  if (typeof prev === "boolean") {
    if (rawString === "true") return true;
    if (rawString === "false") return false;
    return rawString;
  }
  if (prev !== null && typeof prev === "object") {
    try {
      return JSON.parse(rawString);
    } catch {
      return rawString;
    }
  }
  if (typeof prev === "string") {
    const t = rawString.trim();
    if (
      (t.startsWith("{") && t.endsWith("}")) ||
      (t.startsWith("[") && t.endsWith("]"))
    ) {
      try {
        return JSON.parse(rawString);
      } catch {
        return rawString;
      }
    }
    return rawString;
  }
  return rawString;
}

function rowEditKey(tableId, rowIndex) {
  return `${tableId}::${rowIndex}`;
}

function RowEditIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function RowSaveIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function RowCancelIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

const AIExtractedDataReview = (props) => {
  const { data, onDataChange } = props;

  const structureSig = useMemo(() => dataStructureSignature(data), [data]);
  const [tableInputs, setTableInputs] = useState({});
  const [scalarDeletePending, setScalarDeletePending] = useState(null);
  const [tableRowEdit, setTableRowEdit] = useState(null);

  useEffect(() => {
    setTableInputs(buildTableInputsFromExtractedData(data));
  }, [structureSig]);

  useEffect(() => {
    setTableRowEdit(null);
  }, [structureSig]);

  const beginTableRowEdit = useCallback((tableId, rowIndex, rowObj, fieldKeys) => {
    const draft = {};
    fieldKeys.forEach((f) => {
      draft[f] = scalarToInputString(rowObj[f]);
    });
    setTableRowEdit({ rowKey: rowEditKey(tableId, rowIndex), draft });
  }, []);

  const setTableRowDraftField = useCallback((fieldKey, rawValue) => {
    setTableRowEdit((prev) =>
      prev ? { ...prev, draft: { ...prev.draft, [fieldKey]: rawValue } } : prev
    );
  }, []);

  const cancelTableRowEdit = useCallback(() => setTableRowEdit(null), []);

  const commitTableRowEdit = useCallback(
    (sheetIndex, tableKey, tableId, rowIndex, fieldKeys, draft) => {
      if (!onDataChange || !draft) return;
      const nextPayload = JSON.parse(JSON.stringify(data));
      const arr = nextPayload.data[sheetIndex].data[tableKey];
      const prevRow = data.data[sheetIndex].data[tableKey][rowIndex];
      if (!prevRow || typeof prevRow !== "object") return;
      const newRow = { ...prevRow };
      fieldKeys.forEach((f) => {
        newRow[f] = parseScalarInputForStore(prevRow[f], draft[f] ?? "");
      });
      arr[rowIndex] = newRow;
      onDataChange(nextPayload);
      setTableRowEdit(null);
    },
    [data, onDataChange]
  );

  const setTableDraftField = useCallback((tableId, fieldKey, rawValue) => {
    setTableInputs((prev) => {
      const row = prev[tableId];
      if (!row) return prev;
      const fields = { ...row.fields, [fieldKey]: rawValue };
      const response = commonFunction.isObjectValid(fields);
      return {
        ...prev,
        [tableId]: {
          ...row,
          fields,
          isValid: response.key === response.flag,
        },
      };
    });
  }, []);

  const toggleDeleteIndex = useCallback((tableId, dindex) => {
    setTableInputs((prev) => {
      const row = prev[tableId];
      if (!row) return prev;
      const idx = row.selectedIndex.indexOf(dindex);
      const selectedIndex =
        idx === -1
          ? [...row.selectedIndex, dindex]
          : row.selectedIndex.filter((i) => i !== dindex);
      return { ...prev, [tableId]: { ...row, selectedIndex } };
    });
  }, []);

  const addTableRow = useCallback(
    (sheetIndex, tableKey, tableId, fieldKeys) => {
      if (!onDataChange) return;
      setTableInputs((prev) => {
        const ti = prev[tableId];
        if (!ti?.isValid) return prev;
        const nextPayload = JSON.parse(JSON.stringify(data));
        const sheet = nextPayload.data[sheetIndex];
        const newRow = { ...ti.fields };
        fieldKeys.forEach((f) => {
          if (!(f in newRow)) newRow[f] = "";
        });
        sheet.data[tableKey].push(newRow);
        onDataChange(nextPayload);
        const clearedFields = { ...ti.fields };
        Object.keys(clearedFields).forEach((k) => {
          clearedFields[k] = "";
        });
        return {
          ...prev,
          [tableId]: { ...ti, fields: clearedFields, isValid: false },
        };
      });
    },
    [data, onDataChange]
  );

  const deleteTableRows = useCallback(
    (sheetIndex, tableKey, tableId) => {
      if (!onDataChange) return;
      setTableInputs((prev) => {
        const ti = prev[tableId];
        if (!ti?.selectedIndex?.length) return prev;
        const nextPayload = JSON.parse(JSON.stringify(data));
        const sheetDataObj = nextPayload.data[sheetIndex]?.data;
        if (!sheetDataObj) return prev;
        const arr = sheetDataObj[tableKey];
        if (!Array.isArray(arr)) return prev;
        const indexes = commonFunction.quickSort([...ti.selectedIndex]);
        for (let i = indexes.length - 1; i >= 0; i--) arr.splice(indexes[i], 1);
        const tableRemoved = arr.length === 0;
        if (tableRemoved) {
          delete sheetDataObj[tableKey];
        }
        onDataChange(nextPayload);
        const prefix = `${tableId}::`;
        setTableRowEdit((e) => {
          if (!e || !e.rowKey.startsWith(prefix)) return e;
          if (tableRemoved) return null;
          const rest = e.rowKey.slice(prefix.length);
          const editedRow = parseInt(rest, 10);
          if (!Number.isNaN(editedRow) && indexes.includes(editedRow)) return null;
          return e;
        });
        return { ...prev, [tableId]: { ...ti, selectedIndex: [] } };
      });
    },
    [data, onDataChange]
  );

  const deleteScalarField = useCallback(
    (sheetIndex, fieldKey) => {
      if (!onDataChange) return;
      const nextPayload = JSON.parse(JSON.stringify(data));
      const sheetData = nextPayload.data[sheetIndex]?.data;
      if (!sheetData || !Object.prototype.hasOwnProperty.call(sheetData, fieldKey)) return;
      delete sheetData[fieldKey];
      onDataChange(nextPayload);
    },
    [data, onDataChange]
  );

  const updateScalarField = useCallback(
    (sheetIndex, fieldKey, rawString) => {
      if (!onDataChange) return;
      const sheet = data.data[sheetIndex];
      if (!sheet?.data || !Object.prototype.hasOwnProperty.call(sheet.data, fieldKey)) return;
      const prev = sheet.data[fieldKey];
      const nextPayload = JSON.parse(JSON.stringify(data));
      const sheetData = nextPayload.data[sheetIndex].data;
      sheetData[fieldKey] = parseScalarInputForStore(prev, rawString);
      onDataChange(nextPayload);
    },
    [data, onDataChange]
  );

  const bindEnter = useCallback(
    (e, sheetIndex, tableKey, tableId, fieldKeys) => {
      if (e.keyCode === 13) {
        addTableRow(sheetIndex, tableKey, tableId, fieldKeys);
      }
    },
    [addTableRow]
  );

  if (!data) {
    return null;
  }

  const { filename, data: excelData = [] } = data;

  return (
    <>
      <div className="more-desc" />
      <div className="sub-heading">Uploaded File: <span>{filename}</span></div>
      {excelData.map((el, index) => {
        const { sheet_name, data: sheetData } = el;
        return (
          <div className="form-group has-field" key={sheet_name + index}>
            <div className="meta-container">
              <h4 className="meta-heading">Sheet: {sheet_name}</h4>
              <div className="meta-container">
                {Object.keys(sheetData).map((key) => {
                  const value = sheetData[key];
                  const arrKey = sheet_name + key + index;
                  const keyName = `key-name-${key}-${index}`;
                  const keyValue = `key-value-${key}-${index}`;
                  const tableId = `${index}::${key}`;
                  const combinedKey = buildCombinedSheetFieldKey(sheet_name, key);

                  if (value && Array.isArray(value) && value.length > 0) {
                    const fields = collectRowFieldKeys(value);
                    const ti = tableInputs[tableId] || {
                      selectedIndex: [],
                      isValid: false,
                      fields: Object.fromEntries(fields.map((f) => [f, ""])),
                    };

                    return (
                      <div className="form-group" key={arrKey}>
                        <label htmlFor={keyName}>{combinedKey} <span>*</span></label>
                        <table className="table ai-extracted-table">
                          <thead>
                            <tr>
                              <th>
                                <img
                                  src={dlt}
                                  className={`icon ${ti.selectedIndex.length === 0 || !onDataChange ? "disabled" : ""}`}
                                  alt="Delete selected rows"
                                  onClick={() => deleteTableRows(index, key, tableId)}
                                />
                              </th>
                              {
                                fields.map((fel, findex) => (
                                  <th key={arrKey + findex}>{sanitizeAiMarkerKeySegment(fel)}</th>
                                ))
                              }
                              <th className="edit-cell" aria-label="Edit row">Edit?</th>
                            </tr>
                          </thead>
                          <tbody>
                            {value && Array.isArray(value) &&
                              value.map((del, dindex) => {
                                const rKey = rowEditKey(tableId, dindex);
                                const isRowEditing =
                                  tableRowEdit && tableRowEdit.rowKey === rKey;

                                return (
                                  <tr
                                    key={arrKey + "_" + dindex}
                                    className={isRowEditing ? "on-edit-row" : undefined}
                                  >
                                    <td>
                                      <label className="custom-input" data-type="checkbox">
                                        <input
                                          type="checkbox"
                                          name="troe[]"
                                          disabled={!!isRowEditing}
                                          onChange={() => toggleDeleteIndex(tableId, dindex)}
                                          checked={ti.selectedIndex.indexOf(dindex) > -1}
                                        />
                                        <span className="checkmark"></span>
                                      </label>
                                    </td>
                                    {
                                      fields.map((fel, findex) => (
                                        <td key={arrKey + "_" + dindex + "_" + findex}>
                                          {isRowEditing ? (
                                            <input
                                              type="text"
                                              className="input-field"
                                              autoComplete="off"
                                              value={tableRowEdit.draft[fel] ?? ""}
                                              onChange={(e) =>
                                                setTableRowDraftField(fel, e.target.value)
                                              }
                                            />
                                          ) : (
                                            formatTableCell(del[fel])
                                          )}
                                        </td>
                                      ))
                                    }
                                    <td className="edit-cell">
                                      {onDataChange ? (
                                        isRowEditing ? (
                                          <>
                                            <div
                                              type="button"
                                              className="btn-link no-margin"
                                              title="Save row"
                                              onClick={() => {
                                                if (tableRowEdit?.rowKey !== rKey) return;
                                                commitTableRowEdit(
                                                  index,
                                                  key,
                                                  tableId,
                                                  dindex,
                                                  fields,
                                                  tableRowEdit.draft
                                                );
                                              }}
                                            >
                                              <RowSaveIcon />
                                            </div>
                                            <div
                                              type="button"
                                              className="btn-link no-margin table-row-cancel-btn"
                                              title="Discard changes"
                                              aria-label="Discard changes"
                                              onClick={cancelTableRowEdit}
                                            >
                                              <RowCancelIcon />
                                            </div>
                                          </>
                                        ) : (
                                          <div
                                            type="button"
                                            className="btn-link no-margin"
                                            title="Edit row"
                                            onClick={() =>
                                              beginTableRowEdit(tableId, dindex, del, fields)
                                            }
                                          >
                                            <RowEditIcon />
                                          </div>
                                        )
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            }
                            <tr className="input">
                              <td>
                                <img
                                  src={plus}
                                  className={`plus icon${ti.isValid && onDataChange ? "" : " disabled"}`}
                                  alt="Add row"
                                  onClick={() => addTableRow(index, key, tableId, fields)}
                                />
                              </td>
                              {
                                fields.map((fel, findex) => (
                                  <td key={arrKey + "new-" + findex}>
                                    <input
                                      type="text"
                                      className="input-field"
                                      placeholder={`Data for "${sanitizeAiMarkerKeySegment(fel).replace(/_/g, " ")}"`}
                                      autoComplete="off"
                                      onChange={(e) => setTableDraftField(tableId, fel, e.target.value)}
                                      value={ti.fields[fel] || ""}
                                      onKeyUp={(e) => bindEnter(e, index, key, tableId, fields)}
                                    />
                                  </td>
                                ))
                              }
                              <td className="table-actions-col" />
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  }

                  return (
                    <div className="form-group has-field" key={arrKey}>
                      <div className="flex-container with-margin vertical-middle">
                        <div className="flexbox half">
                          <label htmlFor={keyName}>Key Name <span>*</span></label>
                          <input className="input-field" type="text" name={keyName} id={keyName} value={combinedKey} readOnly />
                        </div>
                        <div className="flexbox half">
                          <label htmlFor={keyValue}>Key Value <span>*</span></label>
                          <input
                            className="input-field"
                            type="text"
                            name={keyValue}
                            id={keyValue}
                            readOnly={!onDataChange}
                            value={scalarToInputString(value)}
                            onChange={(e) => updateScalarField(index, key, e.target.value)}
                          />
                        </div>
                        <div>
                          <label htmlFor={keyName + "-delete"}>Delete?</label>
                          <div className="btn-link no-margin">
                            <img
                              src={dlt}
                              className={`icon${!onDataChange ? " disabled" : ""}`}
                              alt="Remove this field"
                              id={keyName + "-delete"}
                              onClick={() => {
                                if (!onDataChange) return;
                                setScalarDeletePending({
                                  sheetIndex: index,
                                  fieldKey: key,
                                  sheetName: sheet_name,
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )
      })}
      {scalarDeletePending && (
        <DeleteConfirmModal
          disableStatus={false}
          desc={`Delete field "${scalarDeletePending.fieldKey}" from sheet "${scalarDeletePending.sheetName}"? This cannot be undone.`}
          buttonText="Delete"
          isLoading={false}
          close={() => setScalarDeletePending(null)}
          doDelete={() => {
            deleteScalarField(
              scalarDeletePending.sheetIndex,
              scalarDeletePending.fieldKey
            );
            setScalarDeletePending(null);
          }}
        />
      )}
    </>
  )
}

export default AIExtractedDataReview;
